import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { useCart, formatPrice } from '../../hooks/useCart';
import { useNavigate } from 'react-router-dom';

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const CartDrawer: React.FC<CartDrawerProps> = ({ open, onClose }) => {
  const { items, removeItem, totalPrice, clearCart, maxItems, getCartInterval } = useCart();
  const navigate = useNavigate();
  const cartInterval = getCartInterval();

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 400,
          maxWidth: '100%',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon />
            <Typography variant="h6">Shopping Cart</Typography>
            {items.length > 0 && (
              <>
                <Typography variant="body2" color="text.secondary">
                  ({items.length}/{maxItems})
                </Typography>
                <Chip 
                  label={`${cartInterval} billing`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label="close cart">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Cart Items */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {items.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 4,
                textAlign: 'center',
              }}
            >
              <ShoppingCartIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Your cart is empty
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Add market subscriptions to get started
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {items.map((item) => (
                <React.Fragment key={item.priceId}>
                  <ListItem sx={{ px: 2, py: 2 }}>
                    <ListItemText
                      primary={item.productName || item.marketName}
                      secondary={
                        item.productType === 'subscription' 
                          ? `${formatPrice(item.price)} / ${item.interval === 'monthly' ? 'month' : 'year'}`
                          : `${formatPrice(item.price)} - One-time purchase`
                      }
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="remove from cart"
                        onClick={() => removeItem(item.priceId)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* Footer */}
        {items.length > 0 && (
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            {/* Total Price */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Total ({cartInterval})
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                {formatPrice(totalPrice)} / {cartInterval === 'monthly' ? 'month' : 'year'}
              </Typography>
            </Box>

            {/* Checkout Button */}
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleCheckout}
              sx={{ mb: 1 }}
            >
              Proceed to Checkout
            </Button>

            {/* Clear Cart Button */}
            <Button
              variant="outlined"
              fullWidth
              size="small"
              onClick={clearCart}
              color="error"
            >
              Clear Cart
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};